// Constants
import { isNull, RED, GREEN, BLUE, YELLOW } from '../../constants';
const DEFAULT_TIMEOUT = 3000;

export const ADD_ANNOUNCEMENT = 'ADD_ANNOUNCEMENT';
export const UPDATE_ANNOUNCEMENT = 'UPDATE_ANNOUNCEMENT';
export const REMOVE_ANNOUNCEMENT = 'REMOVE_ANNOUNCEMENT';
export const REMOVE_ANNOUNCEMENT_BY_MESSAGE = 'REMOVE_ANNOUNCEMENT_BY_MESSAGE';
export const CLEAR_ERRORS = 'CLEAR_ERRORS';
export const CLEAR_ANNOUNCEMENTS = 'CLEAR_ANNOUNCEMENTS';
export const CLEAR_INFOS = 'CLEAR_INFOS';
/** @type {{ ERROR: 'error', WARNING: 'warning', SUCCESS: 'success', INFO: 'info' }} */
export const AnnouncementSeverity = {
  ERROR: 'error',
  WARNING: 'warning',
  SUCCESS: 'success',
  INFO: 'info'
};

/**
 * `actionType` names an action the bar offers rather than carrying a callback,
 * so announcements stay serializable in Redux state. AlertList maps the name to
 * a handler.
 * @typedef {{id: string, title?: string, message?: string, severity?: keyof AnnouncementSeverity, color?: import('../../constants').Color, actionType?: 'REDIAL_EMERGENCY_CALL', actionLabel?: string, showClose?: boolean, timeout?: number | null}} Announcement
 */

/**
 * @param {Omit<Announcement, "id">} announcement
 */
export const addAnnouncement = (announcement) => ({
  type: ADD_ANNOUNCEMENT,
  announcement
});

/**
 * Replaces an existing announcement in place, matched on id.
 * @param {Announcement} announcement
 */
export const updateAnnouncement = (announcement) => ({
  type: UPDATE_ANNOUNCEMENT,
  announcement
});

export const removeAnnouncement = (id) => ({
  type: REMOVE_ANNOUNCEMENT,
  id
});

export const removeAnnouncementByMessage = (message) => ({
  type: REMOVE_ANNOUNCEMENT_BY_MESSAGE,
  message
});

export const clearErrors = () => ({
  type: CLEAR_ERRORS
});

export const clearAnnouncements = () => ({
  type: CLEAR_ANNOUNCEMENTS
});

export const clearInfos = () => ({
  type: CLEAR_INFOS
});

/**
 * Shows an announcement for a given message/title (at least one of them is required)
 * 
 * Timeout defaults to 3000ms, setting to null will disable the timeout.
 * @param {function} dispatch The Redux dispatch function
 * @param {Omit<Announcement, "id">} announcement
 */
export const setAnnouncement = (
  dispatch,
  announcement
) => {
  window.scrollTo(0, 0);

  if (isNull(announcement) || ((announcement.message ?? '') === '' && (announcement.message ?? '') === '')) {
    switch (announcement?.severity) {
      case 'ERROR':
        announcement = { title: 'Error: something went wrong. Please try again later.', ...(announcement ?? {}) };
        break;
      case 'WARNING':
        announcement = { title: 'Warning!', ...(announcement ?? {}) };
        break;
      case 'SUCCESS':
        announcement = { title: 'Success!', ...(announcement ?? {}) };
        break;
      default:
        announcement = { title: 'An unknown event occurred.', ...(announcement ?? {}) };
    }
  }

  // Add New Announcement to App State
  const newAnnouncement = addAnnouncement(announcement);
  dispatch(newAnnouncement);

  // Remove the New Announcement After Timeout (3 sec default)
  if (announcement?.timeout !== null) {
    setTimeout(() => dispatch(removeAnnouncement(newAnnouncement.announcement.id)), announcement.timeout ?? DEFAULT_TIMEOUT);
  }
};

/**
 * How each Twilio call status is presented. Covers the whole lifecycle, not
 * just failures: staff need to see that a dispatch is under way from the moment
 * it is triggered, and watch it progress, rather than only hearing about it if
 * it goes wrong.
 *
 * `isFailure` drives whether the bar offers a redial.
 */
const EMERGENCY_CALL_PRESENTATION = {
  'queued': {
    title: 'Dispatching automated 911 call',
    detail: 'The call has been queued.',
    severity: 'INFO', color: BLUE, isFailure: false
  },
  'initiated': {
    title: 'Dispatching automated 911 call',
    detail: 'The call is being placed.',
    severity: 'INFO', color: BLUE, isFailure: false
  },
  'ringing': {
    title: 'Automated 911 call is ringing',
    detail: 'Waiting for an answer.',
    severity: 'WARNING', color: YELLOW, isFailure: false
  },
  'in-progress': {
    title: 'Automated 911 call connected',
    detail: 'The alert message is playing.',
    severity: 'SUCCESS', color: GREEN, isFailure: false
  },
  'completed': {
    title: 'Automated 911 call completed',
    detail: 'The call was answered and has ended.',
    severity: 'SUCCESS', color: GREEN, isFailure: false
  },
  'no-answer': {
    title: 'Automated 911 call did not reach anyone',
    detail: 'The call was placed and rang, but nobody answered.',
    severity: 'ERROR', color: RED, isFailure: true
  },
  'busy': {
    title: 'Automated 911 call did not reach anyone',
    detail: 'The line was busy.',
    severity: 'ERROR', color: RED, isFailure: true
  },
  'canceled': {
    title: 'Automated 911 call did not reach anyone',
    detail: 'The call was canceled before anyone answered.',
    severity: 'ERROR', color: RED, isFailure: true
  },
  'failed': {
    title: 'Automated 911 call did not reach anyone',
    detail: 'The call was placed but could not be completed.',
    severity: 'ERROR', color: RED, isFailure: true
  },
  'rejected': {
    title: 'Automated 911 call did not reach anyone',
    detail: 'The call could not be placed at all.',
    severity: 'ERROR', color: RED, isFailure: true
  }
};

/**
 * Stable announcement id for a given call, so progress updates replace the bar
 * rather than stacking up alongside it.
 * @param {{ sid: string | null, createdAt: string }} call
 */
export const emergencyCallAnnouncementId = (call) => (
  `emergency-call-${call.sid ?? `rejected-${call.createdAt}`}`
);

/**
 * Builds the announcement describing an emergency call's current state.
 * @param {{ sid: string | null, status: string, durationSeconds: number | null, errorCode: number | null, createdAt: string }} call
 * @returns {Announcement}
 */
export const buildEmergencyCallAnnouncement = (call) => {
  const presentation = EMERGENCY_CALL_PRESENTATION[call.status] ?? {
    title: 'Automated 911 call',
    detail: `The call reported status "${call.status}".`,
    severity: 'INFO',
    color: BLUE,
    isFailure: false
  };

  let message = presentation.detail;

  if (call.errorCode != null) {
    message += ` (Twilio error ${call.errorCode}.)`;
  }

  if (presentation.isFailure) {
    message += ' Redial, or dial 911 manually.';
  }
  else if (call.status === 'completed' && call.durationSeconds != null) {
    message += ` Duration: ${call.durationSeconds}s.`;
  }

  return {
    id: emergencyCallAnnouncementId(call),
    title: presentation.title,
    message,
    severity: presentation.severity,
    color: presentation.color,
    // Only a failed dispatch needs a redial; an in-flight one is already going.
    actionType: presentation.isFailure ? 'REDIAL_EMERGENCY_CALL' : undefined,
    actionLabel: presentation.isFailure ? 'Redial' : undefined,
    showClose: true,
    timeout: null
  };
};

/**
 * Shows the first banner for an emergency call. Scrolls into view, since this
 * is the moment staff need to notice it.
 * @param {*} dispatch
 */
export const announceEmergencyCall = (dispatch, call) => {
  setAnnouncement(dispatch, buildEmergencyCallAnnouncement(call));
};

/**
 * Updates the banner for a call already being shown.
 *
 * Uses updateAnnouncement rather than setAnnouncement so the page is not
 * scrolled to the top on every status change: a single call moves through
 * queued, initiated, ringing and completed, and yanking the view four times
 * would fight whoever is managing the incident.
 * @param {*} dispatch
 */
export const updateEmergencyCallAnnouncement = (dispatch, call) => {
  dispatch(updateAnnouncement(buildEmergencyCallAnnouncement(call)));
};

/**
 * Should be called whenever any alert is received
 * @param {*} dispatch
 * @param {{status: import('../../lib').AlertType, room?: import('../../lib').Room, floor?: import('../../lib').Floor, building?: import('../../lib').Building}} updatedStatus
 */
export const announceSchoolStatus = (dispatch, { status, room, floor, building }) => {
  /** @type {keyof AnnouncementSeverity | undefined} */
  let severity;

  if (status.getDashboardStatus() === 'Normal') {
    severity = 'INFO';
  }
  else if (status.getDashboardStatus() === 'All Clear') {
    severity = 'SUCCESS';
  }
  else if (status.getDashboardStatus() === 'Admin' || status.getDashboardStatus() === 'Medical') {
    severity = 'WARNING';
  }
  else if (status.getDashboardStatus() === 'Emergency') {
    severity = 'ERROR';
  }

  let message = `Room${Number.isNaN(parseInt(room?.getIdentifier())) ? ':' : ''} ${room?.getIdentifier() ?? '[UNKNOWN]'}`;

  if (floor) {
    message += `, Floor${Number.isNaN(parseInt(floor?.getIdentifier())) ? ':' : ''} ${floor.getIdentifier()}`;
  }

  if (building) {
    message += `, Building${Number.isNaN(parseInt(building.getName())) ? ':' : ''} ${building.getName()}`;
  }

  dispatch(removeAnnouncementByMessage(message));

  /** @type {Omit<Announcement, 'id'>} */
  const announcement = {
    id: room?.getID() ?? null,
    title: status.getDashboardStatus(),
    message,
    color: status.getColor(),
    severity,
    timeout: null
  };
  setAnnouncement(dispatch, announcement);
};
